"use client";

import { useRef } from "react";
import Image from "next/image";
import Container from "@/components/ui/Container";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { useNearViewport } from "@/hooks/useNearViewport";

type Client = {
  name: string;
  logo?: string;
  logoDark?: string;
  logoLight?: string;
  showName?: boolean;
  imgClassName?: string;
};

const CLIENTS: Client[] = [
  {
    name: "CoinDCX",
    logoDark: "/images/customers/coindcx-logo-white.svg",
    logoLight: "/images/customers/coindcx-logo-brand.svg",
  },
  {
    name: "Shido",
    logoDark: "/images/customers/shido-logo-dark.svg",
    logoLight: "/images/customers/shido-logo-light.svg",
  },
  {
    name: "Skoda",
    logoDark: "/images/customers/skoda-logo-dark-theme-exact.svg",
    logoLight: "/images/customers/skoda-logo-light-theme-exact.svg",
    showName: true,
  },
  {
    name: "Gems Pocket",
    logoDark: "/images/customers/gems-pocket-logo-dark.svg",
    logoLight: "/images/customers/gems-pocket-logo-light.svg",
  },
  {
    name: "Triskel",
    logoDark: "/images/customers/triskel-logo-dark.svg",
    logoLight: "/images/customers/triskel-logo-light.svg",
  },
  {
    name: "Qubetics",
    logoDark: "/images/customers/qubetics-logo-from-source-dark.svg",
    logoLight: "/images/customers/qubetics-logo-from-source-light.svg",
  },
  {
    name: "XSPA",
    logoDark: "/images/customers/xspa-logo-dark-theme.svg",
    logoLight: "/images/customers/xspa-logo-light-theme.svg",
    imgClassName: "h-8 w-auto max-w-[140px] object-contain",
  },
  {
    name: "EVO Europe",
    logoDark: "/images/customers/evo-europe-logo-dark-theme.svg",
    logoLight: "/images/customers/evo-europe-logo-light-theme.svg",
  },
  {
    name: "Enifeno",
    logoDark: "/images/customers/enifeno-logo-dark-theme.svg",
    logoLight: "/images/customers/enifeno-logo-light-theme.svg",
  },
  {
    name: "Cause Coin",
    logoDark: "/images/customers/cause-coin-logo-dark-theme.svg",
    logoLight: "/images/customers/cause-coin-logo-light-theme.svg",
  },
];

export default function TrustStrip() {
  const sectionRef = useRef<HTMLElement>(null);
  const isNear = useNearViewport(sectionRef);

  useGSAP(
    () => {
      if (!sectionRef.current || !isNear) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Label fades in first
        gsap.from(".trust-label", {
          autoAlpha: 0,
          y: 12,
          duration: 0.5,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 90%",
          },
        });

        // Client pills: set hidden first, then reveal on scroll
        gsap.set(".trust-pill", { autoAlpha: 0, y: 20, scale: 0.92 });

        ScrollTrigger.batch(".trust-pill", {
          start: "top 95%",
          onEnter: (batch) => {
            gsap.to(batch, {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              stagger: 0.06,
              duration: 0.5,
              ease: "back.out(1.4)",
              overwrite: true,
            });
          },
        });
      });
    },
    { scope: sectionRef, dependencies: [isNear] },
  );

  return (
    <section
      ref={sectionRef}
      aria-label="Customers and partners"
      className="relative border-b border-border-subtle bg-bg-surface py-8"
    >
      {/* Edge fades — matches LogoStrip */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-bg-surface to-transparent" />
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-bg-surface to-transparent" />

      <Container>
        <p className="trust-label mb-6 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-text-muted">
          Trusted by leading protocols and institutions
        </p>

        <ul className="flex flex-wrap items-center justify-center gap-3">
          {CLIENTS.map((client) => {
            const hasLogo = Boolean(client.logo || client.logoDark || client.logoLight);
            const imgCls = client.imgClassName ?? "h-6 w-auto max-w-[110px] object-contain";
            return (
              <li
                key={client.name}
                className="trust-pill trust-pill-glow flex h-12 min-w-[120px] items-center justify-center gap-2 rounded-full border border-border-subtle bg-bg-card px-6 text-sm font-medium text-text-secondary opacity-80 transition-all duration-200 hover:border-ink/30 hover:text-text-primary hover:opacity-100"
              >
                {client.logo && (
                  <Image
                    src={client.logo}
                    alt={`${client.name} logo`}
                    width={120}
                    height={28}
                    className={imgCls}
                  />
                )}
                {client.logoDark && (
                  <Image
                    src={client.logoDark}
                    alt={`${client.name} logo`}
                    width={120}
                    height={28}
                    className={`logo-dark-only ${imgCls}`}
                  />
                )}
                {client.logoLight && (
                  <Image
                    src={client.logoLight}
                    alt={`${client.name} logo`}
                    width={120}
                    height={28}
                    className={`logo-light-only ${imgCls}`}
                  />
                )}
                {(!hasLogo || client.showName) && client.name}
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}
